import React from "react";
import { Form, Input, Card, Alert, Space } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import { setCredentials, selectUser } from "../../store/authSlice";
import { homePathForRole } from "../../components/ProtectedRoute";

/**
 * Password change screen. Doubles as the forced first-login step: an account
 * provisioned with a temporary password is redirected here and the API refuses
 * every other endpoint until this succeeds.
 */
const ChangePassword = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const { mutate, loading } = useMutations();

  const forced = Boolean(user?.mustChangePassword);

  const onFinish = async (values) => {
    await mutate("changePassword", {
      endpoint: "/auth/change-password",
      method: "post",
      payload: {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
      // The response carries a fresh token pair: the previous access token still
      // says "must change password" and the old refresh token is already dead.
      onSuccess: (tokens) => {
        dispatch(setCredentials(tokens));
        form.resetFields();
        navigate(homePathForRole(tokens?.role ?? user?.role), { replace: true });
      },
    });
  };

  return (
    <div style={{ maxWidth: 480, margin: "48px auto" }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <h2 style={{ margin: 0 }}>Change password</h2>
            <div style={{ color: "#6b7280" }}>
              Signed in as <strong>{user?.username}</strong>
            </div>
          </div>

          {forced && (
            <Alert
              type="warning"
              showIcon
              message="Choose a new password"
              description="Your account is still using the temporary password it was created with. Set a new one to continue."
            />
          )}

          <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="currentPassword"
              label={forced ? "Temporary password" : "Current password"}
              rules={[{ required: true, message: "Required" }]}
            >
              <Input.Password autoFocus />
            </Form.Item>

            <Form.Item
              name="newPassword"
              label="New password"
              rules={[
                { required: true, message: "Required" },
                { min: 10, message: "At least 10 characters" },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                  message: "Must contain an uppercase letter, a lowercase letter and a digit",
                },
              ]}
            >
              <Input.Password />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm new password"
              dependencies={["newPassword"]}
              rules={[
                { required: true, message: "Required" },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || getFieldValue("newPassword") === value
                      ? Promise.resolve()
                      : Promise.reject(new Error("The two passwords do not match")),
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>

            <Button
              variant="primary"
              block
              htmlType="submit"
              loading={loading?.changePassword}
            >
              Save password
            </Button>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default ChangePassword;
